import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Users, FileWarning } from 'lucide-react';

export function BillingForm({
  customers, doctors,
  selectedCustomer, setSelectedCustomer,
  patientName, setPatientName,
  patientType, setPatientType,
  selectedDoctor, setSelectedDoctor,
  paymentMethod, setPaymentMethod,
  himsRxId, setHimsRxId,
  hasScheduledDrugs,
  prescriberName, setPrescriberName,
  prescriberAddress, setPrescriberAddress,
  showComplianceWarning,
  dpdpConsent, setDpdpConsent,
  consentForReminders, setConsentForReminders
}) {
  const doctorOpts = [];
  for (let i = 0; i < doctors.length; i++) {
    doctorOpts.push(<SelectItem key={doctors[i].id} value={doctors[i].id}>{doctors[i].name}</SelectItem>);
  }

  return (
    <>
      {/* Customer Selection Section */}
      <div className="p-2 bg-blue-50 rounded-lg mb-3 border border-blue-200">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-blue-600" />
          <span className="text-xs font-semibold text-blue-800">Customer</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-blue-700">Select Customer</Label>
            <Select value={selectedCustomer || 'walk-in'} onValueChange={function(v) {
              if (v === 'walk-in') {
                setSelectedCustomer('');
                setPatientName('');
              } else {
                setSelectedCustomer(v);
                const cust = customers.find(function(c) { return c.id === v; });
                if (cust) setPatientName(cust.name);
              }
            }}>
              <SelectTrigger className="h-8 text-sm" data-testid="customer-select">
                <SelectValue placeholder="Walk-in Customer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="walk-in">Walk-in Customer</SelectItem>
                {customers.map(function(cust) {
                  return <SelectItem key={cust.id} value={cust.id}>{cust.name} {cust.phone ? '(' + cust.phone + ')' : ''}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          {!selectedCustomer && (
            <div>
              <Label className="text-xs text-blue-700">Or Enter Name</Label>
              <Input className="h-8 text-sm" placeholder="Walk-in name" value={patientName} onChange={function(e) { setPatientName(e.target.value); }} data-testid="patient-name-input" />
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
        <div>
          <Label className="text-xs">Type</Label>
          <Select value={patientType} onValueChange={setPatientType}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="walk-in">Walk-in</SelectItem>
              <SelectItem value="referred">Referred</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {patientType === 'referred' && (
          <div>
            <Label className="text-xs">Doctor</Label>
            <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{doctorOpts}</SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-xs">Pay</Label>
          <Select value={paymentMethod} onValueChange={setPaymentMethod}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="card">Card</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mb-3">
        <Label className="text-xs">HIMS RX ID</Label>
        <Input className="h-8 text-sm" value={himsRxId} onChange={function(e) { setHimsRxId(e.target.value); }} />
      </div>

      {/* Schedule H1/X Compliance Section */}
      {hasScheduledDrugs && (
        <div className={"p-3 rounded-lg mb-3 " + (showComplianceWarning ? "bg-red-50 border border-red-300" : "bg-amber-50 border border-amber-200")}>
          <div className="flex items-center gap-2 mb-2">
            <FileWarning className="w-4 h-4 text-amber-600" />
            <span className="text-xs font-semibold text-amber-800">COMPLIANCE REQUIRED - Schedule H1/X Drugs</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-amber-800">Prescriber Name *</Label>
              <Input
                className={"h-8 text-sm " + (showComplianceWarning && !prescriberName ? "border-red-400" : "")}
                placeholder="Dr. Name"
                value={prescriberName}
                onChange={function(e) { setPrescriberName(e.target.value); }}
                data-testid="prescriber-name"
              />
            </div>
            <div>
              <Label className="text-xs text-amber-800">Prescriber Address *</Label>
              <Input
                className={"h-8 text-sm " + (showComplianceWarning && !prescriberAddress ? "border-red-400" : "")}
                placeholder="Clinic/Hospital Address"
                value={prescriberAddress}
                onChange={function(e) { setPrescriberAddress(e.target.value); }}
                data-testid="prescriber-address"
              />
            </div>
          </div>
        </div>
      )}

      {/* DPDP Consent Section */}
      {(patientName || selectedCustomer) && (
        <div className="p-2 bg-slate-50 rounded-lg mb-3">
          <p className="text-xs font-medium text-slate-600 mb-2">DPDP Act Consent</p>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="dpdp-consent"
                checked={dpdpConsent}
                onCheckedChange={setDpdpConsent}
                data-testid="dpdp-consent"
              />
              <Label htmlFor="dpdp-consent" className="text-xs text-slate-600 cursor-pointer">
                Patient consents to data storage
              </Label>
            </div>
            {dpdpConsent && (
              <div className="flex items-center gap-2 ml-4">
                <Checkbox
                  id="refill-consent"
                  checked={consentForReminders}
                  onCheckedChange={setConsentForReminders}
                  data-testid="refill-consent"
                />
                <Label htmlFor="refill-consent" className="text-xs text-slate-500 cursor-pointer">
                  Also consent for refill reminders
                </Label>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
